<template>
  <div>
    <div v-if="fetching">
      <div class="flex-grow flex items-center justify-center h-full">
        <Spinner class="w-8" />
      </div>
    </div>
    <div v-else class="w-full pr-3 flex-grow overflow-auto sm:pr-10 pl-2 pb-2">
      <div v-if="projectFeaturesCount" class="pl-1 font-500 mb-2 text-13">Active</div>
      <div v-if="projectFeaturesCount" class="grid gap-4 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 pt-1">
        <ListCard v-for="(feature, key) in features" :key="key" class="h-20 py-2 bg-white rounded-20 cursor-pointer" @click="onFeatureClick(key)">
          <div class="flex justify-between items-center w-full flex-wrap -mt-3">
            <div class="flex items-center mt-3 w-full">
              <div class="bg-primary p-2 rounded-6 mr-2">
                <Icon v-if="getIcon(key)" :name="getIcon(key)" class="w-4 h-4 text-white" />
              </div>

              <div class="flex items-start truncate flex-col">
                <span class="font-600 mr-2 truncate">{{ getName(key) }}</span>
                <span class="font-500 mr-2 text-12 text-gray-500">{{ getEnabledDescription(key) }}</span>
              </div>
              <Toggle :modelValue="features[key].enabled" dense class="ml-auto flex-shrink-0" @update:modelValue="onEnableChange(key)" />
            </div>
          </div>
        </ListCard>
      </div>
      <div v-if="availableFeaturesCount" class="pl-1 font-500 mb-2 mt-6 text-13">Available</div>
      <div class="grid gap-4 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 pt-1">
        <ListCard
          v-for="feature in availableFeatures"
          :key="feature.value"
          :class="{
            hidden: feature.value in features,
          }"
          height-class="h-25"
          class="py-2 bg-white rounded-20 cursor-pointer"
          @click="onFeatureClick(feature.value)"
        >
          <div class="flex justify-between items-center w-full flex-wrap -mt-3">
            <div class="flex items-center mt-3">
              <div class="bg-primary p-2 rounded-6 mr-2">
                <Icon :name="feature.icon" class="w-4 h-4 text-white" />
              </div>
              <div class="flex items-center flex-wrap">
                <div class="font-500 mr-2 w-full">{{ feature.label }}</div>
                <div class="font-500 mr-2 text-12 text-gray-500">{{ feature.description }}</div>
              </div>
            </div>
          </div>
        </ListCard>
      </div>
    </div>
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';
import EnableUIFeatureModal from '@/components/project/EnableUIFeatureModal.vue';
import DeleteModal from '../../../modals/DeleteModal.vue';

export default {
  name: 'UIFeatures',
  data() {
    return {
      fetching: false,
    };
  },
  computed: {
    ...mapGetters(['getProjectFeatures']),
    projectId() {
      return this.$route.params.projectId;
    },
    features() {
      const features = structuredClone(this.getProjectFeatures());
      Object.keys(features).forEach((key) => {
        if (!this.availableFeatures.some((f) => f.value === key && features[key].enabled)) {
          delete features[key];
        }
      });
      return features;
    },
    projectFeaturesCount() {
      return Object.keys(this.features).length;
    },
    availableFeatures() {
      return [
        {
          label: 'Data Conversations',
          value: 'conversations',
          description: 'Enable feature to allow users to chat with AI.',
          enabledDescription: 'Allowing users to chat with AI',
          icon: 'conversations',
        },
        {
          label: 'Reporting Dashboard Collections',
          value: 'collections',
          description: 'Enable feature to allow users to create natural language dashboards.',
          enabledDescription: 'Allowing users to create natural language dashboards',
          icon: 'collections',
        },
        {
          label: 'Data Sources',
          value: 'data_sources',
          description: 'Enable feature to allow users to manage their own data sources.',
          enabledDescription: 'Allowing users to manage their own data sources',
          icon: 'hub',
        },
        {
          label: 'Profile',
          value: 'profile',
          description: 'Enable feature to allow users to update their own profile information.',
          enabledDescription: 'Allowing users to update their own profile information',
          icon: 'profile',
        },
        {
          label: 'AI Deep Research Reports',
          value: 'deep_research_reports',
          description: 'Enable feature to allow users to create scheduled reports using AI Deep Research.',
          enabledDescription: 'Allowing users to create scheduled reports',
          icon: 'query_stats',
        },
        {
          label: 'Data Transformation',
          value: 'data_transformation',
          description: 'Enable feature to allow users to transform data files into structured documents',
          enabledDescription: 'Allowing users to transform data files',
          icon: 'convert_to_text',
        },
      ];
    },
    availableFeaturesCount() {
      return this.availableFeatures.filter((item) => !(item.value in this.features)).length;
    },
  },
  methods: {
    ...mapActions(['fetchProjectFeatures', 'updateProjectFeatures', 'fetchProjectFlows', 'fetchProjectDataAdapters']),
    getIcon(key) {
      return this.availableFeatures.find((f) => f.value === key)?.icon;
    },
    getName(key) {
      return this.availableFeatures.find((f) => f.value === key)?.label;
    },
    getValue(key) {
      return this.availableFeatures.find((f) => f.value === key)?.value;
    },
    getDescription(key) {
      return this.availableFeatures.find((f) => f.value === key)?.description;
    },
    getEnabledDescription(key) {
      return this.availableFeatures.find((f) => f.value === key)?.enabledDescription;
    },
    onEnableChange(feature) {
      const title = this.features[feature].enabled
        ? this.$t('projects.ui_features.disable_feature', { feature: this.getName(feature) })
        : this.$t('projects.ui_features.enable_feature', { feature: this.getName(feature) });

      const subtitle = this.features[feature].enabled
        ? this.$t('projects.ui_features.disable_feature_description', { feature: this.getName(feature) })
        : this.$t('projects.ui_features.enable_feature_description', { feature: this.getName(feature) });
      this.$showModal(DeleteModal, {
        title,
        subtitle,
        type: this.features[feature].enabled ? 'error' : 'primary',
        onConfirm: async () => {
          const features = structuredClone(this.features);
          features[feature].enabled = !features[feature].enabled;
          await this.updateProjectFeatures({ project_id: this.projectId, features });
        },
      });
    },
    onFeatureClick(feature) {
      const featureName = this.getName(feature);
      const featureValue = this.getValue(feature);
      const modal = this.$showModal(
        EnableUIFeatureModal,
        { feature, featureName, description: this.getDescription(feature), featureConfig: feature in this.features ? this.features[feature] : undefined },
        {
          save: async (config) => {
            const payload = { ...this.features, [feature]: config };
            if (featureValue === 'deep_research_reports' && config?.config?.reports?.length === 0) {
              payload.deep_research_reports.enabled = false;
            }
            await this.updateProjectFeatures({ project_id: this.projectId, features: payload });
            modal.close();
          },
        },
      );
    },
  },
  watch: {
    projectId: {
      async handler() {
        this.fetching = true;
        await this.fetchProjectFeatures({ project_id: this.projectId });
        await this.fetchProjectFlows({ projectId: this.projectId });
        await this.fetchProjectDataAdapters({ projectId: this.projectId });
        this.fetching = false;
      },
      immediate: true,
    },
  },
};
</script>
